// Dashboard table for N-outcome sports (soccer Home/Away/Draw today). The
// binary GameTable assumes one away/home market pair per game; soccer is
// three independent binary markets (Away win / Home win / Draw), so each
// event renders through the SAME GameRows component the binary table uses —
// away Y/N row, home Y/N row, Draw row beneath — mirroring how GameDetail's
// Game Lines section lays out the NBA 1H 3-way winner. The Draw row is a
// `continuation` of the pair (same gameIndex), so all three outcomes share
// one zebra background and the event reads as a single 3-way market.
//
// Clicks hand the chosen (event, outcome, venue) back to ValueDashboard,
// which synthesizes the per-outcome GameRow (`${event.key}|multi|${code}`)
// for the side panel + trade ticket — the table itself never builds tickets.
import type {
  MarketSide,
  MultiOutcomeEvent,
  PriceMode,
  SeriesOutcome,
  TeamSide,
} from './types';
import type { UserPositionSlice, UserOrderSlice } from '@/hooks/useUserKalshiPOS';
import { Th } from '@/components/atoms/TableCells';
import { GameRows } from './GameTable';
import { multiOutcomeDrawRow, multiOutcomePairRow, multiOutcomeRoles } from './multiOutcome';

export interface OutcomeClickOpts {
  priceCents?: number;
  initialOrderSide?: 'yes' | 'no';
  postOnly?: boolean;
}

interface MultiOutcomeTableProps {
  events: MultiOutcomeEvent[];
  /** Table title ("Soccer Moneylines"). */
  titleLabel?: string;
  /** Kalshi series prefixes shown beside the title, as on GameTable. */
  seriesTicker?: string;
  marketSide: MarketSide;
  priceMode: PriceMode;
  /** Key of the outcome whose detail panel is open — the dashboard's
   *  expandedGameKey, shaped `${event.key}|multi|${outcome.code}` by
   *  synthesizeFromMultiOutcome. Null when no multi-outcome panel is up. */
  expandedOutcomeKey: string | null;
  /** Row click (no opts) opens/toggles the outcome's panel; pill + ladder
   *  clicks pass price/side opts through to seed the trade ticket. */
  onOutcomeClick: (
    event: MultiOutcomeEvent,
    outcome: SeriesOutcome,
    venue: 'kalshi' | 'polymarket',
    opts?: OutcomeClickOpts,
  ) => void;
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
}

export function MultiOutcomeTable({
  events,
  titleLabel,
  seriesTicker,
  marketSide,
  priceMode,
  expandedOutcomeKey,
  onOutcomeClick,
  positionByTicker,
  ordersByTicker,
}: MultiOutcomeTableProps) {
  // Soonest kickoff first; unknown starts sink within the list.
  const sorted = [...events].sort((a, b) => {
    const cmp =
      (a.startTimeMs ?? Number.POSITIVE_INFINITY) - (b.startTimeMs ?? Number.POSITIVE_INFINITY);
    if (cmp !== 0) return cmp;
    return a.awayCode.localeCompare(b.awayCode);
  });

  // Same header semantics as GameTable: pre-game rows show kickoff time,
  // started rows show the score, mixed slates label both.
  const startedCount = sorted.filter((e) => e.gameState !== 'pre').length;
  const scoreColLabel =
    sorted.length === 0 || startedCount === sorted.length
      ? 'Score'
      : startedCount === 0
        ? 'Time'
        : 'Time / Score';

  return (
    <div className="bg-card border-border rounded-lg border">
      <div className="border-border flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <h2 className="text-foreground text-lg font-semibold">{titleLabel ?? 'Moneylines'}</h2>
          {seriesTicker && (
            <>
              <span className="text-muted-foreground text-xs">&mdash;</span>
              <span className="text-muted-foreground font-mono text-xs">{seriesTicker}</span>
            </>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse border-spacing-0">
          <thead className="bg-muted/50 border-border border-b">
            <tr>
              <Th className="w-28">Date</Th>
              <Th className="w-48">Team</Th>
              <Th
                className="w-28 text-center"
                title="Kickoff time + countdown until the game starts, then the live/final score."
              >
                {scoreColLabel}
              </Th>
              <Th
                className="w-16 text-center"
                title="Unabated consensus win probability (pre-game / live); blank once the game is final."
              >
                Odds
              </Th>
              <Th className="w-32 text-center">Kalshi</Th>
              <Th className="w-16 text-right" title="Total BID/ASK contracts">K Vol</Th>
              <Th className="w-24 text-right" title="Total $ resting in book (bids + asks)">K Liq</Th>
              <Th
                className="w-16 text-right"
                title={`Best-bid depth on the active ${marketSide.toUpperCase()} side. Green <50k / Yellow 50–300k / Red ≥300k.`}
              >
                K Q
              </Th>
              <Th
                className="w-16 text-right"
                title="Kalshi best-bid depth change over the last 1m on the active side."
              >
                K Δ/m
              </Th>
              <Th className="w-32 text-center">Poly</Th>
              <Th className="w-16 text-right" title="Total BID/ASK contracts">P Vol</Th>
              <Th className="w-24 text-right" title="Total $ resting in book (bids + asks)">P Liq</Th>
              <Th
                className="w-16 text-right"
                title="Polymarket best-bid depth change over the last 1m on the active side."
              >
                P Δ/m
              </Th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((e, eventIndex) => {
              const { away, home, draw } = multiOutcomeRoles(e);
              const pair = away && home ? multiOutcomePairRow(e, away, home) : null;
              const drawSynth = draw ? multiOutcomeDrawRow(e, draw) : null;

              // Which row+side the open panel's outcome lives on. The panel
              // key is `${event.key}|multi|${code}` regardless of venue.
              const prefix = `${e.key}|multi|`;
              const expandedCode = expandedOutcomeKey?.startsWith(prefix)
                ? expandedOutcomeKey.slice(prefix.length)
                : null;
              const pairExpandedSide: TeamSide | null =
                expandedCode && away && expandedCode === away.code
                  ? 'away'
                  : expandedCode && home && expandedCode === home.code
                    ? 'home'
                    : null;
              const drawExpanded = expandedCode != null && draw != null && expandedCode === draw.code;

              const outcomeFor = (key: string, side: TeamSide): SeriesOutcome | undefined =>
                pair && key === pair.key ? (side === 'away' ? away : home) : draw;
              const handleToggle = (key: string, side: TeamSide) => {
                const o = outcomeFor(key, side);
                if (!o) return;
                onOutcomeClick(e, o, o.kalshi ? 'kalshi' : 'polymarket');
              };
              const handleTrade = (
                key: string,
                side: TeamSide,
                venue: 'kalshi' | 'polymarket',
                opts?: OutcomeClickOpts,
              ) => {
                const o = outcomeFor(key, side);
                if (!o) return;
                onOutcomeClick(e, o, venue, opts ?? {});
              };

              const shared = {
                marketSide,
                priceMode,
                onToggleExpand: handleToggle,
                onTradeClick: handleTrade,
                positionByTicker,
                ordersByTicker,
                showScores: true,
              };
              // groupSelected/groupExtendsBelow tie the pair + Draw into ONE
              // selection group: whichever outcome is expanded, the blue box
              // wraps all three rows (pair top rail → Draw bottom rail).
              return [
                pair && (
                  <GameRows
                    key={pair.key}
                    game={pair}
                    gameIndex={eventIndex}
                    expandedTeamSide={pairExpandedSide}
                    groupSelected={drawExpanded}
                    groupExtendsBelow={drawSynth != null}
                    {...shared}
                  />
                ),
                drawSynth && (
                  <GameRows
                    key={drawSynth.key}
                    game={drawSynth}
                    gameIndex={eventIndex}
                    continuation={pair != null}
                    singleSide="away"
                    oddsOutcome="draw"
                    expandedTeamSide={drawExpanded ? 'away' : null}
                    groupSelected={pairExpandedSide != null}
                    {...shared}
                  />
                ),
              ];
            })}
          </tbody>
        </table>
      </div>

      {events.length === 0 && (
        <div className="text-muted-foreground p-8 text-center">No games available</div>
      )}
    </div>
  );
}
